import { useI18n } from '../i18n';
import { Header } from './Header';
import { UpdateBanner } from './UpdateBanner';

/**
 * Coquille applicative : bandeau de mise à jour, lien d'évitement, en-tête et
 * zone de contenu principale. Colonne EXACTEMENT à la hauteur du viewport
 * (`h-dvh` + `overflow-hidden`) : la coquille NE défile PAS — le seul conteneur
 * défilant de l'app est la grille virtualisée (interne). L'en-tête ne rétrécit
 * pas (`shrink-0`) ; la zone principale prend le reste (`flex-1 min-h-0`) et
 * délègue son défilement à son contenu. Les écrans de T2b s'insèrent via
 * `children` ; la navigation via `nav`.
 */
interface AppShellProps {
  children: React.ReactNode;
  /** Slot de navigation (onglets) transmis à l'en-tête — implémenté en T2b. */
  nav?: React.ReactNode;
  /** Actions de fin d'en-tête (T7a : menu utilisateur). */
  headerActions?: React.ReactNode;
}

export function AppShell({ children, nav, headerActions }: AppShellProps): React.JSX.Element {
  const { t } = useI18n();

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-surface text-fg">
      <UpdateBanner />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-30 focus:m-2 focus:rounded-md focus:bg-brand focus:px-3 focus:py-2 focus:text-brand-fg"
      >
        {t('a11y.skipToContent')}
      </a>
      <Header nav={nav} actions={headerActions} />
      {/*
       * `relative` (sans offset = no-op visuel ; n'affecte PAS les `position:fixed`) :
       * établit le BLOC CONTENEUR de tout descendant `position:absolute` des pages
       * rendues via `<Outlet/>`. Sans lui, un `sr-only` (Tailwind pose `position:absolute`)
       * enfoui SANS ancêtre positionné s'ancre au « Initial Containing Block » du document
       * et GONFLE la barre de défilement du document → une 2e barre déplace toute la page
       * (fil d'Ariane compris). Même classe de bug corrigée en v0.48.0 pour `/outils/dedup`.
       * Ancrer ici couvre TOUTES les pages en une fois (garde par-page en défense en profondeur).
       */}
      <main
        id="main"
        className="relative mx-auto flex w-full min-h-0 max-w-7xl flex-1 flex-col px-4 py-4"
      >
        {children}
      </main>
    </div>
  );
}
